/**
 * Copilot VOICE API — typed client for the ai-orchestrator voice surface
 * (speech-to-text via the shared LiteLLM Whisper gateway + optional ASR
 * correction/translation).
 *
 * Mirrors:
 *   services-python/ai-orchestrator-service/src/routes/voice_api.py
 *     POST /api/v1/ai/voice/transcribe  (multipart: audio, language?, correct?, translate_to?)
 *     GET  /api/v1/ai/voice/config      ({enabled, max_upload_mb, max_upload_bytes, accepted})
 *
 * Error mapping (honest — never fabricates a transcript):
 *   - 402 (out of AI credits): `apiFetch` (src/lib/api/client.ts) ALREADY detects
 *     every backend insufficient-credits shape and dispatches the global
 *     `psn:ai-credits-insufficient` window event (CreditGuardHost listens and
 *     shows the shared refill modal/toast) before this module ever sees the
 *     rejection — we deliberately do NOT build a second, voice-specific
 *     paywall here. Callers just let a 402 `ApiError` propagate.
 *   - 503 `{detail:"voice_unavailable", reason}`: the Whisper model/gateway is
 *     unreachable — mapped to {@link VoiceUnavailableError}. The caller must
 *     degrade honestly (disable the mic control), never retry silently forever
 *     and never show a fake transcript.
 *   - 413 (upload exceeds the admin-configured size limit): mapped to
 *     {@link VoiceTooLargeError} with a clear French message.
 */

import { ApiError, apiFetch } from '@/lib/api/client';

const BASE = '/api/v1/ai/voice';

// ---------------------------------------------------------------------------
// Types (mirror TranscribeResponse / GET /config in voice_api.py)
// ---------------------------------------------------------------------------

export interface VoiceConfig {
  enabled: boolean;
  max_upload_mb: number;
  max_upload_bytes: number;
  accepted: string[];
}

export interface TranscribeOptions {
  /** ISO language hint for Whisper (improves accuracy). */
  language?: string;
  /** Run the ASR-cleanup pass server-side. Defaults true (matches the backend default). */
  correct?: boolean;
  /** Target language code/name to translate the (corrected) transcript into. */
  translate_to?: string;
}

export interface TranscribeResult {
  /** The raw Whisper output, before any correction pass. */
  raw_text: string;
  /** The (possibly corrected) transcript in the spoken language. */
  text: string;
  /** Present only when `translate_to` was supplied AND translation succeeded. */
  translated_text?: string | null;
  detected_language?: string | null;
  duration_sec?: number | null;
  credits_used: number;
  correction_applied: boolean;
  translation_applied: boolean;
}

// ---------------------------------------------------------------------------
// Typed errors — honest degrade, never a fabricated transcript.
// ---------------------------------------------------------------------------

/**
 * The transcription model/gateway is unreachable (HTTP 503
 * `{detail:"voice_unavailable"}`). Callers must show a disabled/unavailable
 * state — never invent a transcript.
 */
export class VoiceUnavailableError extends Error {
  readonly reason?: string;
  constructor(reason?: string) {
    super(reason || 'Reconnaissance vocale momentanément indisponible.');
    this.name = 'VoiceUnavailableError';
    this.reason = reason;
  }
}

/** The recorded audio exceeds the admin-configured upload limit (HTTP 413). */
export class VoiceTooLargeError extends Error {
  constructor(message?: string) {
    super(message || 'Audio trop long — réduisez la durée de votre enregistrement.');
    this.name = 'VoiceTooLargeError';
  }
}

// ---------------------------------------------------------------------------
// GET /config — cached for the session (mirrors getCopilotUploadConfig).
// ---------------------------------------------------------------------------

let _configCache: VoiceConfig | null = null;
let _configPromise: Promise<VoiceConfig> | null = null;

/**
 * Voice-input capability + limits. Cached for the session (pass `force` to
 * re-check, e.g. after an admin flips the setting). Never throws: on a
 * network/backend failure it returns an honest `enabled:false` shape so the
 * UI degrades to a disabled control instead of erroring.
 */
export async function getVoiceConfig(opts?: { force?: boolean }): Promise<VoiceConfig> {
  if (!opts?.force && _configCache) return _configCache;
  if (!opts?.force && _configPromise) return _configPromise;
  _configPromise = apiFetch<VoiceConfig>(`${BASE}/config`)
    .then((cfg) => {
      _configCache = cfg;
      return cfg;
    })
    .catch(() => {
      const fallback: VoiceConfig = {
        enabled: false,
        max_upload_mb: 0,
        max_upload_bytes: 0,
        accepted: [],
      };
      _configCache = fallback;
      return fallback;
    })
    .finally(() => {
      _configPromise = null;
    });
  return _configPromise;
}

/** Test-only: clear the cached config so each test starts from a clean slate. */
export function _resetVoiceConfigCacheForTests(): void {
  _configCache = null;
  _configPromise = null;
}

// ---------------------------------------------------------------------------
// Extension guessing — the backend is liberal about content-type (never 415s
// on it), but a real filename/extension helps some server-side sniffing.
// ---------------------------------------------------------------------------

function extensionForMimeType(mimeType: string): string {
  const t = mimeType.toLowerCase();
  if (t.includes('webm')) return 'webm';
  if (t.includes('ogg')) return 'ogg';
  if (t.includes('mp4') || t.includes('m4a') || t.includes('aac')) return 'm4a';
  if (t.includes('wav')) return 'wav';
  if (t.includes('mpeg') || t.includes('mp3')) return 'mp3';
  return 'webm';
}

// ---------------------------------------------------------------------------
// POST /transcribe
// ---------------------------------------------------------------------------

/**
 * Uploads recorded `audio` for speech-to-text (+ optional correction/
 * translation). Throws:
 *   - {@link VoiceUnavailableError} on 503 (model/gateway unreachable)
 *   - {@link VoiceTooLargeError} on 413 (over the size limit)
 *   - the original `ApiError` for everything else, INCLUDING 402 (out of AI
 *     credits) — `apiFetch` has already dispatched the global insufficient-
 *     credits event by the time this rejects, so the caller should let it
 *     propagate to the shared paywall rather than building its own.
 */
export async function transcribe(audio: Blob, opts: TranscribeOptions = {}): Promise<TranscribeResult> {
  const form = new FormData();
  const filename = audio instanceof File ? audio.name : `voice-note.${extensionForMimeType(audio.type)}`;
  form.append('audio', audio, filename);
  if (opts.language) form.append('language', opts.language);
  if (opts.correct === false) form.append('correct', 'false');
  if (opts.translate_to) form.append('translate_to', opts.translate_to);

  try {
    return await apiFetch<TranscribeResult>(`${BASE}/transcribe`, { method: 'POST', body: form });
  } catch (err) {
    if (err instanceof ApiError) {
      // The ONLY 503 this endpoint answers with is {detail:"voice_unavailable",
      // reason} (see voice_api.py) — an honest degrade, never a fabricated
      // transcript. Any 503 from /transcribe is treated the same way.
      if (err.status === 503) {
        const body = err.body as { reason?: string } | null;
        throw new VoiceUnavailableError(typeof body?.reason === 'string' ? body.reason : undefined);
      }
      if (err.status === 413) {
        const body = err.body as { detail?: string } | null;
        throw new VoiceTooLargeError(typeof body?.detail === 'string' ? body.detail : undefined);
      }
    }
    // 402 (insufficient AI credits) and everything else propagate unchanged.
    throw err;
  }
}

/** True when `error` is specifically the transcription model being unreachable. */
export function isVoiceUnavailable(error: unknown): error is VoiceUnavailableError {
  return error instanceof VoiceUnavailableError;
}

/** True when `error` is specifically the audio exceeding the size limit. */
export function isVoiceTooLarge(error: unknown): error is VoiceTooLargeError {
  return error instanceof VoiceTooLargeError;
}

/** True when the failure is an out-of-AI-credits 402 (the global paywall already fired). */
export function isVoiceOutOfCredits(error: unknown): boolean {
  return error instanceof ApiError && error.status === 402;
}
